import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import Icon from './Icon';
import HelpIcon from './help/HelpIcon';
import './CashCountModal.css';

function CashCountModal({ onClose, onConfirm, expectedAmount }) {
  const { t } = useTranslation();

  // Denominations for ARS (could be configurable in the future)
  const denominations = [
    { value: 10000, label: '$10.000' },
    { value: 2000, label: '$2.000' },
    { value: 1000, label: '$1.000' },
    { value: 500, label: '$500' },
    { value: 200, label: '$200' },
    { value: 100, label: '$100' },
    { value: 50, label: '$50' },
    { value: 20, label: '$20' },
    { value: 10, label: '$10' },
  ];

  const [counts, setCounts] = useState({});
  const [coins, setCoins] = useState('');
  const [total, setTotal] = useState(0);
  const [notes, setNotes] = useState('');
  const [blindCount, setBlindCount] = useState(true);

  useEffect(() => {
    let sum = 0;

    // Sum bills
    Object.entries(counts).forEach(([value, count]) => {
      sum += parseInt(value) * (parseInt(count) || 0);
    });

    // Sum coins
    sum += parseFloat(coins) || 0;

    setTotal(sum);
  }, [counts, coins]);

  const handleCountChange = (value, count) => {
    setCounts((prev) => ({
      ...prev,
      [value]: count,
    }));
  };

  const handleSubmit = () => {
    const cashCount = {
      ...counts,
      coins: parseFloat(coins) || 0,
    };

    // Remove empty counts
    Object.keys(cashCount).forEach((key) => {
      if (!cashCount[key]) delete cashCount[key];
    });

    onConfirm({
      closingAmount: total,
      cashCount,
      notes,
    });
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('es-AR', {
      style: 'currency',
      currency: 'ARS',
    }).format(amount || 0);
  };

  const difference = total - (expectedAmount || 0);

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal cash-count-modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>{t('cashRegister.cashCount.title')}</h2>
          <button type="button" className="close-btn" onClick={onClose}>
            <Icon name="close" />
          </button>
        </div>

        <div className="modal-body">
          <div className="cash-count-grid">
            {denominations.map((denom) => (
              <div key={denom.value} className="denomination-row">
                <label>{denom.label}</label>
                <div className="input-wrapper">
                  <input
                    type="number"
                    min="0"
                    placeholder="0"
                    value={counts[denom.value] || ''}
                    onChange={(e) => handleCountChange(denom.value, e.target.value)}
                  />
                  <span className="subtotal">
                    {formatCurrency(denom.value * (parseInt(counts[denom.value]) || 0))}
                  </span>
                </div>
              </div>
            ))}

            <div className="denomination-row coins-row">
              <label>{t('cashRegister.cashCount.coins')}</label>
              <div className="input-wrapper">
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  placeholder="0.00"
                  value={coins}
                  onChange={(e) => setCoins(e.target.value)}
                />
                <span className="subtotal">{formatCurrency(parseFloat(coins) || 0)}</span>
              </div>
            </div>
          </div>

          <div className="count-summary">
            <div className="summary-row total-row">
              <span>{t('cashRegister.cashCount.totalCounted')}:</span>
              <strong>{formatCurrency(total)}</strong>
            </div>

            <div className="blind-count-toggle">
              <label>
                <input
                  type="checkbox"
                  checked={blindCount}
                  onChange={(e) => setBlindCount(e.target.checked)}
                />
                {t('cashRegister.cashCount.hideExpected')}
              </label>
            </div>

            {!blindCount && (
              <>
                <div className="summary-row">
                  <span>
                    {t('cashRegister.cashCount.expected')}:{' '}
                    <HelpIcon topicKey="cashRegister.expectedVsDeclared" />
                  </span>
                  <span>{formatCurrency(expectedAmount)}</span>
                </div>
                <div
                  className={`summary-row difference ${difference >= 0 ? 'positive' : 'negative'}`}
                >
                  <span>{t('cashRegister.difference')}:</span>
                  <span>
                    {difference >= 0 ? '+' : ''}
                    {formatCurrency(difference)}
                  </span>
                </div>
              </>
            )}
          </div>

          <div className="form-group">
            <label>{t('cashRegister.notes')}</label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={2}
              placeholder={t('cashRegister.closeShiftModal.addNotes')}
            />
          </div>
        </div>

        <div className="modal-footer">
          <button type="button" className="btn btn-ghost" onClick={onClose}>
            {t('common.cancel')}
          </button>
          <button type="button" className="btn btn-primary" onClick={handleSubmit}>
            {t('cashRegister.closeShiftModal.confirm')}
          </button>
        </div>
      </div>
    </div>
  );
}

export default CashCountModal;
